import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import "../style/financialreport.css";
import { PageHeader, SearchInput, EmptyState } from "../components/admin/AdminUI";
import { formatDmy, fileDateStamp, formatPaidDate } from "../utils/dateFormat";
import ClassPaymentModal, { METHOD_LABEL } from "./financial/ClassPaymentModal";

// Data dihitung ulang backend setiap kali diambil, jadi halaman ini cukup
// mengambil ulang secara berkala supaya pembayaran baru langsung terlihat.
const REFRESH_MS = 30000;
const PAGE_SIZE = 50;

// Kolom bulan, mengikuti sheet "KEUANGAN CEA" (September → Juli). Februari
// tidak ada kelas berbayar (Masterclass gratis), jadi tidak ditampilkan.
const MONTHS = [
  { code: "OT", label: "September", late: false },
  { code: "NT", label: "October", late: false },
  { code: "BD", label: "November", late: false },
  { code: "BLC", label: "December", late: false },
  { code: "BCMF", label: "January", late: true },
  { code: "HH", label: "March", late: true },
  { code: "CBH", label: "April", late: true },
  { code: "CBD", label: "May", late: true },
  { code: "BWG", label: "June", late: true },
  { code: "MMM", label: "July", late: true },
];

const rp = (n) => (typeof n === "number" ? `Rp${n.toLocaleString("id-ID")}` : "-");

const STATUS_TEXT = { paid: "Paid", awaiting: "Awaiting", partial: "Partial", unpaid: "Unpaid" };
const PAYER_STATUS = {
  paid: "Paid",
  awaiting_payment: "Awaiting",
  payment_failed: "Failed",
  not_billed: "Not billed",
  unpaid: "Not billed",
  covered: "Covered",
};
const PAYER_LABEL = { Student: "Student", PIC: "PIC", "Other Email": "Other" };

const isSettled = (r) => r.totalBillable > 0 && r.paidCount >= r.totalBillable;

// Ekspektasi pemasukan = semua yang harus dibayar student + PIC + Other
// (harga normal dikurangi subsidi CEA).
const expectedOf = (v) => (v.totals ? v.totals.student + v.totals.pic + v.totals.other : null);

const ZERO_TOTALS = { normal: 0, cea: 0, pic: 0, other: 0, student: 0, paid: 0, outstanding: 0 };

const FinancialReportPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [errorMe, setErrorMe] = useState(false);
  const [rows, setRows] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [search, setSearch] = useState("");
  const [fSource, setFSource] = useState("all");
  const [fProgram, setFProgram] = useState("all");
  const [fPlan, setFPlan] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fBatch, setFBatch] = useState(null); // null = belum diisi (tunggu currentBatch)
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // { row, cls } — sel yang sedang diedit

  const fetchReport = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const res = await axios.get("/financial-report");
      const data = res.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setCurrentBatch(data.currentBatch ? String(data.currentBatch) : null);
      setUpdatedAt(new Date());
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) setErrorMe(true);
      else if (!silent) setRows([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setLocationAdmin({ pathname: "/financialreport" });
    axios.get("/meAdmin").catch(() => setErrorMe(true));
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh: berkala selama tab terlihat + saat admin kembali ke tab.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") fetchReport({ silent: true });
    };
    const id = setInterval(tick, REFRESH_MS);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", tick);
    };
  }, [fetchReport]);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  // Batch default = batch yang sedang berjalan (seperti tab angkatan di sheet).
  useEffect(() => {
    if (fBatch === null && currentBatch) setFBatch(currentBatch);
  }, [currentBatch, fBatch]);
  const batch = fBatch || "all";

  // Angka yang ditampilkan: seluruh pendaftaran (All batches) atau hanya
  // kelas di batch terpilih — seperti sheet keuangan yang dibuat per angkatan.
  const viewOf = useCallback(
    (r) => {
      if (batch === "all") return r;
      return (
        r.byBatch?.[batch] || {
          totals: r.totals ? ZERO_TOTALS : null,
          paidCount: 0,
          totalBillable: 0,
        }
      );
    },
    [batch],
  );

  const programOptions = useMemo(() => [...new Set(rows.map((r) => r.program))].filter(Boolean).sort(), [rows]);
  const batchOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.batchJoined) set.add(String(r.batchJoined));
      r.classes.forEach((c) => c.batch && set.add(String(c.batch)));
    });
    if (currentBatch) set.add(currentBatch);
    return [...set].filter((b) => Number(b) > 0).sort((a, b) => Number(b) - Number(a));
  }, [rows, currentBatch]);

  // Student masuk batch X kalau dia punya kelas di batch X (sama seperti
  // sheet per angkatan: student lama yang mengulang kelas ikut tercatat).
  const inBatch = useCallback(
    (r) =>
      batch === "all" ||
      String(r.batchJoined) === batch ||
      r.classes.some((c) => String(c.batch) === batch && c.status !== "prior" && c.status !== "repeat"),
    [batch],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!inBatch(r)) return false;
      if (fSource !== "all" && r.source !== fSource) return false;
      if (fProgram !== "all" && r.program !== fProgram) return false;
      if (fPlan !== "all" && r.paymentPlan !== fPlan) return false;
      if (fStatus === "settled" && !isSettled(viewOf(r))) return false;
      if (fStatus === "outstanding" && isSettled(viewOf(r))) return false;
      if (!q) return true;
      return [r.name, r.email, r.studentId, r.localChurch, r.church?.churchName, r.church?.picName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search, fSource, fProgram, fPlan, fStatus, inBatch, viewOf]);

  useEffect(() => setPage(1), [search, fSource, fProgram, fPlan, fStatus, batch]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (a, r) => {
          const t = viewOf(r).totals;
          if (!t) return a;
          a.normal += t.normal;
          a.cea += t.cea;
          a.student += t.student;
          a.pic += t.pic + t.other;
          a.paid += t.paid;
          a.outstanding += t.outstanding;
          return a;
        },
        { normal: 0, cea: 0, student: 0, pic: 0, paid: 0, outstanding: 0 },
      ),
    [filtered, viewOf],
  );

  // Kolom "Other" (payer email lain di CEA To Church) hanya muncul kalau ada.
  const showOther = filtered.some((r) => r.split.other > 0 || (viewOf(r).totals?.other || 0) > 0);

  // Persentase pembagian untuk angka yang sedang ditampilkan (semua batch /
  // batch terpilih). Subsidi dicatat per pendaftaran, jadi batch lama bisa
  // berbeda dari batch baru — kalau campuran, ditampilkan "mixed".
  const pctText = (r, key) => {
    const v = viewOf(r);
    if (v.effectiveSplit) return `${v.effectiveSplit[key]}%`;
    return v.totalBillable ? "mixed" : "—";
  };
  const pctPositive = (r, key) => {
    const v = viewOf(r);
    return v.effectiveSplit ? v.effectiveSplit[key] > 0 : (v.totals?.[key] || 0) > 0;
  };
  const SUB = ["Student", "PIC", ...(showOther ? ["Other"] : []), "CEA", "Paid On"];

  const monthTitle = (m) => {
    if (batch === "all") return m.label;
    const b = Number(batch);
    return `${m.label} ${m.late ? b + 2022 : b + 2021}`;
  };

  // Kelas student untuk satu kode — utamakan yang ada di batch terpilih.
  const pickClass = (r, code) => {
    const list = r.classes.filter((c) => c.code === code);
    if (list.length === 0) return null;
    if (batch !== "all") {
      const inSel = list.find((c) => String(c.batch) === batch);
      if (inSel) return inSel;
    }
    const current = list.filter((c) => c.status !== "prior" && c.status !== "repeat");
    const pool = current.length ? current : list;
    return pool.slice().sort((a, b) => (Number(b.batch) || 0) - (Number(a.batch) || 0))[0];
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasFilter =
    search || fSource !== "all" || fProgram !== "all" || fPlan !== "all" || fStatus !== "all";
  const resetFilters = () => {
    setSearch("");
    setFSource("all");
    setFProgram("all");
    setFPlan("all");
    setFStatus("all");
  };

  const churchOf = (r) => (r.church ? r.church.churchName : r.localChurch) || "-";
  const picFrom = (r) =>
    r.church ? `${r.church.picName} — ${r.church.churchName}${r.church.churchCity ? `, ${r.church.churchCity}` : ""}` : "";

  // 1 Year Program yang punya kelas hasil 1 Month (sebelum upgrade).
  const monthlyCarried = (r) =>
    r.classes.filter((c) => c.paidAsMonthly && (batch === "all" || String(c.batch) === batch)).length;
  const programText = (r) => {
    const n = monthlyCarried(r);
    return n ? `${r.program} (incl. ${n} class${n > 1 ? "es" : ""} paid as 1 Month)` : r.program;
  };

  // ── Export Excel dengan layout yang sama (header 2 baris, bulan di-merge) ──
  const exportExcel = () => {
    const left = [
      "STUDENT ID", "FULL NAME", "EMAIL", "ANGKATAN", "ASAL GEREJA", "ROLE", "PROGRAM", "PAYMENT PLAN",
      "HARGA DASAR", "PAYMENT (% STUDENT BAYAR)", "SUBSIDI CEA (%)", "SUBSIDI CEA (Rp)",
      "PIC (%)", "PIC (Rp)", "PIC DARI",
      ...(showOther ? ["OTHER (%)", "OTHER (Rp)"] : []),
      "HARGA AKHIR STUDENT",
    ];
    const right = ["KELAS", "EKSPEKTASI PEMASUKAN", "PEMASUKAN REAL", "SELISIH"];
    const row1 = [...left];
    const row2 = left.map(() => "");
    const merges = left.map((_, i) => ({ s: { r: 0, c: i }, e: { r: 1, c: i } }));
    MONTHS.forEach((m) => {
      const start = row1.length;
      SUB.forEach((sub, i) => {
        row1.push(i === 0 ? `${monthTitle(m)} (${m.code})` : "");
        row2.push(sub);
      });
      merges.push({ s: { r: 0, c: start }, e: { r: 0, c: start + SUB.length - 1 } });
    });
    right.forEach((h) => {
      merges.push({ s: { r: 0, c: row1.length }, e: { r: 1, c: row1.length } });
      row1.push(h);
      row2.push("");
    });

    const body = filtered.map((r) => {
      const v = viewOf(r);
      const t = v.totals;
      const out = [
        r.studentId, r.name, r.email, r.batchJoined || "", churchOf(r), r.role || "", programText(r),
        r.paymentPlan === "installment" ? "Installment" : "Full",
        t ? t.normal : "", pctText(r, "student"), pctText(r, "cea"), t ? t.cea : "",
        pctText(r, "pic"), t ? t.pic : "", picFrom(r),
        ...(showOther ? [pctText(r, "other"), t ? t.other : ""] : []),
        t ? t.student : "",
      ];
      MONTHS.forEach((m) => {
        const c = pickClass(r, m.code);
        if (!c || !c.amounts || c.status === "prior" || c.status === "repeat") {
          const note = c && c.status === "prior" ? "Earlier batch" : c && c.status === "repeat" ? "Repeat (not charged)" : "";
          SUB.forEach((_, i) => out.push(i === 0 ? note : ""));
          return;
        }
        out.push(c.amounts.student, c.amounts.pic);
        if (showOther) out.push(c.amounts.other);
        out.push(c.amounts.cea);
        out.push(
          (c.status === "paid" ? formatPaidDate(c.paidAt, c.paidDateUnknown) || "Paid" : STATUS_TEXT[c.status] || c.status) +
            (c.paidAsMonthly ? " (paid as 1 Month)" : ""),
        );
      });
      const expected = expectedOf(v);
      out.push(v.totalBillable, expected ?? "", t ? t.paid : "", t ? t.paid - expected : "");
      return out;
    });

    const ws = XLSX.utils.aoa_to_sheet([row1, row2, ...body]);
    ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, batch === "all" ? "All batches" : `Batch ${batch}`);
    XLSX.writeFile(wb, `financial-report${batch === "all" ? "" : `-batch-${batch}`}-${fileDateStamp()}.xlsx`);
  };

  // ── Render helpers ──
  const money = (n, extraClass = "") => (
    <td className={`fr-num ${extraClass}`.trim()}>
      {n == null ? "-" : n === 0 ? <span className="fr-zero">0</span> : rp(n)}
    </td>
  );

  const payerLine = (c, type) => {
    if (c.status === "paid") return null;
    const p = (c.payers || []).find((x) => x.payerType === type);
    if (!p || (c.payers || []).length < 2) return null;
    const cls = p.status === "paid" ? "paid" : p.status === "awaiting_payment" ? "wait" : "no";
    return (
      <span className={`fr-payer-st fr-payer-st--${cls}`}>
        {PAYER_STATUS[p.status] || p.status}
        {p.paidAt ? ` ${formatDmy(p.paidAt)}` : ""}
      </span>
    );
  };

  const monthCells = (r, m) => {
    const c = pickClass(r, m.code);
    if (!c || c.status === "prior" || c.status === "repeat") {
      const clickable = c && c.status === "repeat";
      return (
        <td
          key={m.code}
          colSpan={SUB.length}
          className={`fr-cell--none fr-m-start${clickable ? " fr-clickable" : ""}`}
          onClick={clickable ? () => setEditing({ row: r, cls: c }) : undefined}
          title={clickable ? "Click to add a note" : undefined}
        >
          {!c ? "—" : c.status === "repeat" ? `Repeat class (B${c.batch}) — not charged` : `Earlier batch (B${c.batch})`}
          {c && c.history && c.history.notes > 0 && <i className="bi bi-chat-left-text fr-cell-icon" aria-label="Has notes"></i>}
        </td>
      );
    }
    const cellCls = `fr-cell--${c.status}`;
    const a = c.amounts;
    const otherBatch =
      c.batch && ((batch !== "all" && String(c.batch) !== batch) || (batch === "all" && String(c.batch) !== String(r.batchJoined)));
    const tip = (c.payers || [])
      .map((p) => `${PAYER_LABEL[p.payerType] || p.payerType}: ${PAYER_STATUS[p.status] || p.status}${p.paidAt ? ` (${formatDmy(p.paidAt)})` : ""}`)
      .join("\n");
    const dateCls =
      c.status === "paid" ? "fr-date" : c.status === "unpaid" ? "fr-date--unpaid" : "fr-date--awaiting";
    const open = () => setEditing({ row: r, cls: c });
    const clickProps = {
      onClick: open,
      title: c.editMode === "full" ? "Click to edit this payment" : "Click to view or add a note",
    };
    const edited = c.history && c.history.count - c.history.notes > 0;
    return (
      <React.Fragment key={m.code}>
        <td className={`fr-num fr-m-start fr-clickable ${cellCls}`} {...clickProps}>
          {a ? (a.student ? rp(a.student) : <span className="fr-zero">0</span>) : "-"}
          {payerLine(c, "Student")}
        </td>
        <td className={`fr-num fr-clickable ${cellCls}`} {...clickProps}>
          {a ? (a.pic ? rp(a.pic) : <span className="fr-zero">0</span>) : "-"}
          {payerLine(c, "PIC")}
        </td>
        {showOther && (
          <td className={`fr-num fr-clickable ${cellCls}`} {...clickProps}>
            {a ? (a.other ? rp(a.other) : <span className="fr-zero">0</span>) : "-"}
            {payerLine(c, "Other Email")}
          </td>
        )}
        <td className={`fr-num fr-clickable ${cellCls}`} {...clickProps}>
          {a ? (a.cea ? rp(a.cea) : <span className="fr-zero">0</span>) : "-"}
        </td>
        <td
          className={`fr-clickable ${cellCls}`}
          onClick={open}
          title={[
            tip,
            c.paymentMethod ? `Method: ${METHOD_LABEL[c.paymentMethod] || c.paymentMethod}` : null,
            c.history && c.history.lastNote ? `Note: ${c.history.lastNote.note}` : null,
            c.editMode === "full" ? "Click to edit" : "Click to view or add a note",
          ]
            .filter(Boolean)
            .join("\n")}
        >
          <span className={dateCls}>
            {c.status === "paid" ? formatPaidDate(c.paidAt, c.paidDateUnknown) || "Paid" : STATUS_TEXT[c.status] || c.status}
          </span>
          {c.paidAsMonthly && (
            <span className="fr-batch-tag fr-batch-tag--monthly" title="Taken and paid as 1 Month Course before upgrading">1M</span>
          )}
          {otherBatch && <span className="fr-batch-tag">B{c.batch}</span>}
          {c.paymentMethod === "gateway" && <i className="bi bi-lock-fill fr-cell-icon" aria-label="Paid online — locked"></i>}
          {edited && <i className="bi bi-pencil-fill fr-cell-icon" aria-label="Edited by admin"></i>}
          {c.history && c.history.notes > 0 && <i className="bi bi-chat-left-text fr-cell-icon" aria-label="Has notes"></i>}
        </td>
      </React.Fragment>
    );
  };

  const LEFT_MONEY_COLS = 7 + (showOther ? 2 : 0);

  return (
    <div className="adm-main">
      <PageHeader
        title="Financial Report"
        subtitle="Per-student payments by month — what the student and the PIC must pay, CEA's subsidy, and when each class was paid. Updates automatically when a payment comes in."
        actions={
          <>
            <span className="fr-updated">
              {updatedAt ? `Updated ${updatedAt.toLocaleTimeString("en-GB")}` : ""}
            </span>
            <button type="button" className="adm-btn adm-btn--secondary" onClick={() => fetchReport()} disabled={isLoading}>
              <i className="bi bi-arrow-clockwise"></i> Refresh
            </button>
            <button type="button" className="adm-btn adm-btn--primary" onClick={exportExcel} disabled={isLoading || filtered.length === 0}>
              <i className="bi bi-file-earmark-excel"></i> Export Excel
            </button>
          </>
        }
      />

      <div className="adm-stats">
        <div className="adm-stat">
          <p className="adm-stat-label">Total Normal Price</p>
          <p className="adm-stat-value">{rp(totals.normal)}</p>
          <p className="adm-stat-hint">{filtered.length} students</p>
        </div>
        <div className="adm-stat">
          <p className="adm-stat-label">CEA Subsidy</p>
          <p className="adm-stat-value">{rp(totals.cea)}</p>
        </div>
        <div className="adm-stat" style={{ borderColor: "#fb3104", boxShadow: "inset 3px 0 0 #fb3104" }}>
          <p className="adm-stat-label">Total After Subsidy</p>
          <p className="adm-stat-value">{rp(totals.normal - totals.cea)}</p>
          <p className="adm-stat-hint">What CEA should receive (students + PIC / other)</p>
        </div>
        <div className="adm-stat">
          <p className="adm-stat-label">Students Must Pay</p>
          <p className="adm-stat-value">{rp(totals.student)}</p>
        </div>
        <div className="adm-stat">
          <p className="adm-stat-label">PIC / Other Must Pay</p>
          <p className="adm-stat-value">{rp(totals.pic)}</p>
        </div>
        <div className="adm-stat">
          <p className="adm-stat-label">Collected</p>
          <p className="adm-stat-value" style={{ color: "#16a34a" }}>{rp(totals.paid)}</p>
        </div>
        <div className="adm-stat">
          <p className="adm-stat-label">Outstanding</p>
          <p className="adm-stat-value" style={{ color: "#dc2626" }}>{rp(totals.outstanding)}</p>
        </div>
      </div>

      <div className="adm-toolbar" style={{ flexWrap: "wrap" }}>
        <select className="form-select" style={{ width: "auto", fontWeight: 600 }} value={batch} onChange={(e) => setFBatch(e.target.value)} aria-label="Batch">
          {batchOptions.map((b) => (
            <option key={b} value={b}>
              Batch {b} ({Number(b) + 2021}/{Number(b) + 2022}){b === currentBatch ? " — running" : ""}
            </option>
          ))}
          <option value="all">All batches</option>
        </select>
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, ID, church, PIC" />
        <select className="form-select" style={{ width: "auto" }} value={fSource} onChange={(e) => setFSource(e.target.value)} aria-label="Source">
          <option value="all">All sources</option>
          <option value="Regular">Regular</option>
          <option value="Scholarship">Scholarship</option>
          <option value="CEA To Church">CEA To Church</option>
        </select>
        <select className="form-select" style={{ width: "auto" }} value={fProgram} onChange={(e) => setFProgram(e.target.value)} aria-label="Program">
          <option value="all">All programs</option>
          {programOptions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select className="form-select" style={{ width: "auto" }} value={fPlan} onChange={(e) => setFPlan(e.target.value)} aria-label="Payment plan">
          <option value="all">Full & installment</option>
          <option value="full">Full payment</option>
          <option value="installment">Installment</option>
        </select>
        <select className="form-select" style={{ width: "auto" }} value={fStatus} onChange={(e) => setFStatus(e.target.value)} aria-label="Payment status">
          <option value="all">All statuses</option>
          <option value="outstanding">Has unpaid classes</option>
          <option value="settled">Fully paid</option>
        </select>
        {hasFilter && (
          <button type="button" className="adm-btn adm-btn--ghost" onClick={resetFilters}>
            Reset filters
          </button>
        )}
      </div>

      <div className="adm-card adm-card--flush">
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading financial report…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="bi-cash-coin"
            title="No students to show"
            text={hasFilter || batch !== "all" ? "No student matches the current batch / filter." : "There is no student data yet."}
          />
        ) : (
          <>
            <div className="fr-legend">
              <span><span className="fr-swatch" style={{ background: "#effaf2" }} />Paid (date shown)</span>
              <span><span className="fr-swatch" style={{ background: "#fff8e8" }} />Awaiting / partially paid</span>
              <span><span className="fr-swatch" style={{ background: "#fff" }} />Unpaid</span>
              <span><span className="fr-swatch" style={{ background: "#f7f7f9" }} />Not taken</span>
              <span><span className="fr-batch-tag" style={{ marginLeft: 0 }}>B6</span> class falls in another batch</span>
              <span>
                <span className="fr-batch-tag fr-batch-tag--monthly" style={{ marginLeft: 0 }}>1M</span> taken as 1 Month before upgrading (Rp1.050.000)
              </span>
              <span><i className="bi bi-lock-fill fr-cell-icon" style={{ marginLeft: 0 }}></i> paid online — locked</span>
              <span><i className="bi bi-pencil-fill fr-cell-icon" style={{ marginLeft: 0 }}></i> edited by admin</span>
              <span><i className="bi bi-chat-left-text fr-cell-icon" style={{ marginLeft: 0 }}></i> has notes</span>
              <span className="fr-legend-hint">Click a month to edit or add a note</span>
            </div>
            <div className="fr-wrap">
              <table className="fr-grid">
                <thead>
                  <tr>
                    <th rowSpan={2} className="fr-sticky-1">Student ID</th>
                    <th rowSpan={2} className="fr-sticky-2">Full Name</th>
                    <th rowSpan={2}>Angkatan</th>
                    <th rowSpan={2}>Asal Gereja</th>
                    <th rowSpan={2}>Role</th>
                    <th rowSpan={2}>Program</th>
                    <th colSpan={LEFT_MONEY_COLS} className="fr-h-money">Harga & Pembagian</th>
                    {MONTHS.map((m, i) => (
                      <th key={m.code} colSpan={SUB.length} className={`fr-month${i % 2 ? " fr-month--alt" : ""}`}>
                        {monthTitle(m)} · {m.code}
                      </th>
                    ))}
                    <th colSpan={4} className="fr-h-total">Ringkasan</th>
                  </tr>
                  <tr>
                    <th className="fr-h-money">Harga Dasar</th>
                    <th className="fr-h-money">Student %</th>
                    <th className="fr-h-money">Subsidi CEA</th>
                    <th className="fr-h-money">PIC</th>
                    <th className="fr-h-money">PIC Dari</th>
                    {showOther && <th className="fr-h-money">Other</th>}
                    {showOther && <th className="fr-h-money">Other Email</th>}
                    <th className="fr-h-money">Student Bayar</th>
                    <th className="fr-h-money">PIC Bayar</th>
                    {MONTHS.map((m) =>
                      SUB.map((s) => <th key={`${m.code}-${s}`}>{s}</th>),
                    )}
                    <th className="fr-h-total">Kelas</th>
                    <th className="fr-h-total">Ekspektasi Pemasukan</th>
                    <th className="fr-h-total">Pemasukan Real</th>
                    <th className="fr-h-total">Selisih</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => {
                    const v = viewOf(r);
                    const t = v.totals;
                    const expected = expectedOf(v);
                    const diff = t ? t.paid - expected : null;
                    const otherPayer = r.payers.find((p) => p.payerType === "Other Email");
                    return (
                      <tr key={r.id}>
                        <td className="fr-sticky-1">{r.studentId}</td>
                        <td className="fr-sticky-2">
                          <div className="fr-name">{r.name}</div>
                          <div className="fr-meta">{r.email}</div>
                        </td>
                        <td style={{ textAlign: "center" }}>{r.batchJoined || "-"}</td>
                        <td>{churchOf(r)}</td>
                        <td>{r.role || "-"}</td>
                        <td>
                          {r.program}
                          {monthlyCarried(r) > 0 && (
                            <div className="fr-meta" style={{ color: "#3a45c9", fontWeight: 600 }}>
                              incl. {monthlyCarried(r)} class{monthlyCarried(r) > 1 ? "es" : ""} paid as 1 Month
                            </div>
                          )}
                          <div className="fr-meta">
                            {r.paymentPlan === "installment" ? "Installment" : "Full payment"}
                            {r.source !== "Regular" ? ` · ${r.source}` : ""}
                          </div>
                        </td>
                        {money(t?.normal, "fr-money-col")}
                        <td className="fr-num fr-money-col">{pctText(r, "student")}</td>
                        <td className="fr-num fr-money-col">
                          {t ? rp(t.cea) : "-"}
                          <div className="fr-meta">{pctText(r, "cea")}</div>
                        </td>
                        <td className="fr-num fr-money-col">
                          {t ? (t.pic ? rp(t.pic) : <span className="fr-zero">0</span>) : "-"}
                          {pctPositive(r, "pic") && <div className="fr-meta">{pctText(r, "pic")}</div>}
                        </td>
                        <td className="fr-money-col">
                          {r.church ? (
                            <>
                              {r.church.picName}
                              <div className="fr-meta">
                                {r.church.churchName}
                                {r.church.churchCity ? `, ${r.church.churchCity}` : ""}
                              </div>
                            </>
                          ) : (
                            <span className="fr-zero">—</span>
                          )}
                        </td>
                        {showOther && (
                          <td className="fr-num fr-money-col">
                            {t ? (t.other ? rp(t.other) : <span className="fr-zero">0</span>) : "-"}
                            {pctPositive(r, "other") && <div className="fr-meta">{pctText(r, "other")}</div>}
                          </td>
                        )}
                        {showOther && <td className="fr-money-col">{otherPayer ? otherPayer.payerEmail : <span className="fr-zero">—</span>}</td>}
                        {money(t?.student, "fr-money-col")}
                        {money(t ? t.pic : null, "fr-money-col")}
                        {MONTHS.map((m) => monthCells(r, m))}
                        <td style={{ textAlign: "center" }}>
                          {v.paidCount}/{v.totalBillable}
                        </td>
                        {money(expected)}
                        <td className="fr-num" style={{ color: "#15803d", fontWeight: 600 }}>{t ? rp(t.paid) : "-"}</td>
                        <td className={`fr-num ${diff < 0 ? "fr-neg" : diff > 0 ? "fr-pos" : ""}`}>
                          {diff == null ? "-" : diff === 0 ? <span className="fr-zero">0</span> : rp(diff)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="fr-pager">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} students
                {" · "}Hover a "Paid On" cell to see each payer's payment date.
              </span>
              {pageCount > 1 && (
                <div className="d-flex gap-2">
                  <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                    Previous
                  </button>
                  <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}>
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {editing && (
        <ClassPaymentModal
          row={editing.row}
          cls={editing.cls}
          onClose={() => setEditing(null)}
          onSaved={() => fetchReport({ silent: true })}
        />
      )}
    </div>
  );
};

export default FinancialReportPage;
